/**
=========================================================
* Material Kit 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, Suspense, lazy } from 'react';

// react-router components
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// @mui material components
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Material Kit 2 PRO React themes
import theme from 'assets/theme';

// Material Kit 2 PRO React routes
import routes from 'routes';

// Loading component
import MKBox from 'components/MKBox';
import LoadingSpinner from 'components/LoadingSpinner';

// Lazy load components
const Presentation = lazy(() => import('layouts/pages/presentation'));
const ArticleDetails = lazy(() => import('pages/Blog/Page'));

// Loading fallback component
const LoadingFallback = () => (
  <MKBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <LoadingSpinner variant="dots" size="large" minHeight="60vh" />
  </MKBox>
);

export default function App() {
  const { pathname } = useLocation();

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes
      ?.map((route) => {
        if (route?.collapse) {
          return getRoutes(route.collapse);
        }

        if (route?.route && route?.component) {
          return (
            <Route
              exact
              path={route.route}
              element={<Suspense fallback={<LoadingFallback />}>{route.component}</Suspense>}
              key={route.key || route.route}
            />
          );
        }

        return null;
      })
      .filter(Boolean)
      .flat() || [];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {getRoutes(routes)}
          <Route
            path="/associations-patients"
            element={<Navigate to="/patient-information-resources" replace />}
          />
          <Route
            path=""
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Presentation />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="" />} />
          <Route
            path="/blog/:slug"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <ArticleDetails />
              </Suspense>
            }
          />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
